/**
 * Business Hours Page — shadcn/ui redesign
 *
 * Visual changes only — all state, API calls, and handlers are preserved exactly.
 * Replaced: MUI (Paper/Grid/Card/TextField/Switch/FormControlLabel/Select/Alert/CircularProgress/Chip/Divider)
 *           → shadcn Card / Table layout / Input / Switch / Label / Select / Badge / Sheet / AlertDialog
 * Added:    Compact 7-row schedule table (vs 7 separate cards), saving spinner in button,
 *           Sheet for AutoResponderForm (vs inline Paper), AlertDialog for delete
 *           (vs window.confirm), toast for all feedback (vs inline Alert + setTimeout),
 *           toast for validation (vs alert()), enabled/disabled Badge on responder cards,
 *           skeleton loading, empty state with CTA.
 */
import React, { useState, useEffect } from 'react';
import {
  Clock,
  Save,
  Plus,
  Pencil,
  Trash2,
  MoreHorizontal,
  Loader2,
  BotMessageSquare,
} from 'lucide-react';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import {
  Card,
  CardAction,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '../components/ui/card';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Separator } from '../components/ui/separator';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '../components/ui/sheet';
import { Skeleton } from '../components/ui/skeleton';
import { Switch } from '../components/ui/switch';
import { toast } from '../components/ui/use-toast';
import { cn } from '../lib/utils';
import api from '../services/api';

// ─── Interfaces (unchanged) ───────────────────────────────────────────────────

interface BusinessHour {
  id?: number;
  day_of_week: number;
  open_time: string | null;
  close_time: string | null;
  is_working_day: boolean;
  timezone: string;
  created_at?: string;
  updated_at?: string;
}

interface AutoResponder {
  id?: number;
  name: string;
  trigger_type: string;
  message: string;
  conditions: Record<string, any>;
  is_enabled: boolean;
  delay_seconds: number;
  times_triggered?: number;
  created_at?: string;
  updated_at?: string;
}

// ─── Constants (unchanged) ────────────────────────────────────────────────────

const DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

const TIMEZONES = [
  'UTC',
  'America/New_York',
  'America/Los_Angeles',
  'Europe/London',
  'Europe/Paris',
  'Asia/Tokyo',
  'Asia/Shanghai',
  'Australia/Sydney',
];

const TRIGGER_TYPES = [
  { value: 'outside_business_hours', label: 'Outside Business Hours' },
  { value: 'first_message',          label: 'First Message' },
  { value: 'no_agent_available',     label: 'No Agent Available' },
];

const TRIGGER_CONFIG: Record<string, { badgeClass: string }> = {
  outside_business_hours: { badgeClass: 'border-blue-200 bg-blue-50 text-blue-700 dark:border-blue-800 dark:bg-blue-950 dark:text-blue-300' },
  first_message:          { badgeClass: 'border-violet-200 bg-violet-50 text-violet-700 dark:border-violet-800 dark:bg-violet-950 dark:text-violet-300' },
  no_agent_available:     { badgeClass: 'border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-300' },
};

// ─── Main page component ──────────────────────────────────────────────────────

const BusinessHoursPage: React.FC = () => {
  // ── Existing state (unchanged) ─────────────────────────────────────────────
  const [businessHours, setBusinessHours]       = useState<BusinessHour[]>([]);
  const [autoResponders, setAutoResponders]     = useState<AutoResponder[]>([]);
  const [loading, setLoading]                   = useState(false);
  const [saving, setSaving]                     = useState(false);
  const [editingResponder, setEditingResponder] = useState<AutoResponder | null>(null);

  // ── New UI state (no API changes) ─────────────────────────────────────────
  const [responderSheetOpen, setResponderSheetOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen]     = useState(false);
  const [responderToDelete, setResponderToDelete]   = useState<AutoResponder | null>(null);

  // ── Initialize (unchanged logic) ──────────────────────────────────────────
  const initializeBusinessHours = (): BusinessHour[] =>
    Array.from({ length: 7 }, (_, index) => ({
      day_of_week:    index,
      open_time:      index < 5 ? '09:00' : null,
      close_time:     index < 5 ? '17:00' : null,
      is_working_day: index < 5,
      timezone:       'UTC',
    }));

  useEffect(() => {
    fetchBusinessHours();
    fetchAutoResponders();
  }, []);

  // ── Handlers — logic unchanged; setError/setSuccess → toast ───────────────

  const fetchBusinessHours = async () => {
    setLoading(true);
    try {
      const response = await api.get('/business-hours/');
      if (response.data.length === 0) {
        setBusinessHours(initializeBusinessHours());
      } else {
        const allDays = initializeBusinessHours();
        const merged  = allDays.map((defaultHour) => {
          const existing = response.data.find(
            (bh: BusinessHour) => bh.day_of_week === defaultHour.day_of_week,
          );
          return existing || defaultHour;
        });
        setBusinessHours(merged);
      }
    } catch {
      toast({ title: 'Error', description: 'Failed to fetch business hours.', variant: 'destructive' });
      setBusinessHours(initializeBusinessHours());
    } finally {
      setLoading(false);
    }
  };

  const fetchAutoResponders = async () => {
    try {
      const response = await api.get('/business-hours/auto-responders');
      setAutoResponders(response.data);
    } catch {
      // Silent — page still useful without auto responders
    }
  };

  const handleBusinessHourChange = (
    dayIndex: number,
    field: keyof BusinessHour,
    value: any,
  ) => {
    setBusinessHours((prev) =>
      prev.map((bh, i) => (i === dayIndex ? { ...bh, [field]: value } : bh)),
    );
  };

  const saveBusinessHours = async () => {
    setSaving(true);
    try {
      await api.post('/business-hours/bulk', businessHours);
      toast({ title: 'Business hours saved', description: 'Your schedule has been updated.' });
      fetchBusinessHours();
    } catch {
      toast({ title: 'Error', description: 'Failed to save business hours.', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const handleAutoResponderSubmit = async (responderData: Partial<AutoResponder>) => {
    try {
      if (editingResponder?.id) {
        await api.put(`/business-hours/auto-responders/${editingResponder.id}`, responderData);
        toast({ title: 'Auto responder updated' });
      } else {
        await api.post('/business-hours/auto-responders', responderData);
        toast({ title: 'Auto responder created' });
      }
      fetchAutoResponders();
      setEditingResponder(null);
      setResponderSheetOpen(false);
    } catch {
      toast({ title: 'Error', description: 'Failed to save auto responder.', variant: 'destructive' });
    }
  };

  const handleDeleteRequest = (responder: AutoResponder) => {
    setResponderToDelete(responder);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!responderToDelete?.id) return;
    try {
      await api.delete(`/business-hours/auto-responders/${responderToDelete.id}`);
      toast({ title: 'Auto responder deleted' });
      fetchAutoResponders();
    } catch {
      toast({ title: 'Error', description: 'Failed to delete auto responder.', variant: 'destructive' });
    } finally {
      setResponderToDelete(null);
      setDeleteDialogOpen(false);
    }
  };

  // ── Skeleton loading ───────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="flex flex-1 flex-col gap-4 py-4 px-4 md:gap-6 md:py-6 lg:px-6">
        <div className="space-y-2">
          <Skeleton className="h-7 w-44" />
          <Skeleton className="h-4 w-80" />
        </div>
        <Card>
          <CardHeader>
            <Skeleton className="h-4 w-48" />
            <Skeleton className="h-8 w-32 rounded-md" />
          </CardHeader>
          <CardContent className="p-0">
            {Array.from({ length: 7 }).map((_, i) => (
              <div key={i} className="flex items-center gap-4 border-b px-6 py-4 last:border-0">
                <Skeleton className="h-4 w-24" />
                <Skeleton className="h-5 w-9 rounded-full" />
                <Skeleton className="h-8 w-28 rounded-md" />
                <Skeleton className="h-4 w-4" />
                <Skeleton className="h-8 w-28 rounded-md" />
                <Skeleton className="h-8 w-40 rounded-md ml-auto" />
              </div>
            ))}
          </CardContent>
        </Card>
      </div>
    );
  }

  // ── Main render ────────────────────────────────────────────────────────────
  return (
    <div className="flex flex-1 flex-col gap-4 py-4 px-4 md:gap-6 md:py-6 lg:px-6">

      {/* ── Page header ──────────────────────────────────────────────────────── */}
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Business Hours</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Configure your team's working schedule and automated away responses
        </p>
      </div>

      {/* ── Working Hours Configuration ───────────────────────────────────────── */}
      <Card>
        <CardHeader>
          <div>
            <CardTitle className="text-sm font-medium">Working Hours</CardTitle>
            <CardDescription className="mt-0.5 text-xs">
              Customers will see an away message outside these hours
            </CardDescription>
          </div>
          <CardAction>
            <Button size="sm" onClick={saveBusinessHours} disabled={saving}>
              {saving ? (
                <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
              ) : (
                <Save className="mr-1.5 h-3.5 w-3.5" />
              )}
              {saving ? 'Saving…' : 'Save Changes'}
            </Button>
          </CardAction>
        </CardHeader>

        <CardContent className="p-0">
          {/* 7-row schedule table */}
          <div className="divide-y">
            {businessHours.map((hour, index) => (
              <div
                key={index}
                className={cn(
                  'flex flex-wrap items-center gap-x-4 gap-y-2 px-6 py-3.5',
                  !hour.is_working_day && 'bg-muted/20',
                )}
              >
                {/* Day name */}
                <span
                  className={cn(
                    'w-24 text-sm font-medium',
                    !hour.is_working_day && 'text-muted-foreground',
                  )}
                >
                  {DAYS[index]}
                </span>

                {/* Working day toggle */}
                <div className="flex items-center gap-2">
                  <Switch
                    id={`day-${index}`}
                    checked={hour.is_working_day}
                    onCheckedChange={(checked) =>
                      handleBusinessHourChange(index, 'is_working_day', checked)
                    }
                  />
                  <Label
                    htmlFor={`day-${index}`}
                    className={cn(
                      'cursor-pointer text-xs',
                      hour.is_working_day ? 'text-foreground' : 'text-muted-foreground',
                    )}
                  >
                    {hour.is_working_day ? 'Working' : 'Off'}
                  </Label>
                </div>

                {/* Time inputs + timezone — only when working */}
                {hour.is_working_day ? (
                  <div className="flex flex-1 flex-wrap items-center gap-2">
                    <div className="flex items-center gap-2">
                      <Input
                        type="time"
                        value={hour.open_time || ''}
                        onChange={(e) =>
                          handleBusinessHourChange(index, 'open_time', e.target.value)
                        }
                        className="h-8 w-28 text-xs tabular-nums"
                      />
                      <span className="text-xs text-muted-foreground">to</span>
                      <Input
                        type="time"
                        value={hour.close_time || ''}
                        onChange={(e) =>
                          handleBusinessHourChange(index, 'close_time', e.target.value)
                        }
                        className="h-8 w-28 text-xs tabular-nums"
                      />
                    </div>
                    <Select
                      value={hour.timezone}
                      onValueChange={(v) => handleBusinessHourChange(index, 'timezone', v)}
                    >
                      <SelectTrigger className="ml-auto h-8 w-44 text-xs">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {TIMEZONES.map((tz) => (
                          <SelectItem key={tz} value={tz} className="text-xs">
                            {tz}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                ) : (
                  <p className="flex-1 text-xs text-muted-foreground">Not a working day</p>
                )}
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* ── Auto Responders ───────────────────────────────────────────────────── */}
      <Card>
        <CardHeader>
          <div>
            <CardTitle className="text-sm font-medium">Auto Responders</CardTitle>
            <CardDescription className="mt-0.5 text-xs">
              Automatic replies triggered by specific scenarios
            </CardDescription>
          </div>
          <CardAction>
            <Button
              size="sm"
              variant="outline"
              onClick={() => { setEditingResponder(null); setResponderSheetOpen(true); }}
            >
              <Plus className="mr-1.5 h-3.5 w-3.5" />
              Add Responder
            </Button>
          </CardAction>
        </CardHeader>

        <CardContent className={autoResponders.length === 0 ? undefined : 'pt-0'}>
          {autoResponders.length === 0 ? (
            /* Empty state */
            <div className="flex flex-col items-center justify-center gap-3 py-12">
              <div className="rounded-full bg-muted p-3">
                <BotMessageSquare className="h-6 w-6 text-muted-foreground" />
              </div>
              <div className="text-center">
                <p className="text-sm font-medium text-muted-foreground">No auto responders yet</p>
                <p className="mt-0.5 text-xs text-muted-foreground/70">
                  Add one to send automatic replies outside business hours
                </p>
              </div>
              <Button
                size="sm"
                variant="outline"
                onClick={() => { setEditingResponder(null); setResponderSheetOpen(true); }}
              >
                Add Auto Responder
              </Button>
            </div>
          ) : (
            /* Responder card grid */
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {autoResponders.map((responder) => {
                const triggerLabel = TRIGGER_TYPES.find(
                  (t) => t.value === responder.trigger_type,
                )?.label ?? responder.trigger_type;
                const triggerStyle = TRIGGER_CONFIG[responder.trigger_type] ?? {
                  badgeClass: 'border-border text-muted-foreground',
                };

                return (
                  <Card key={responder.id} className="shadow-sm">
                    <CardHeader>
                      <div className="flex flex-col gap-1.5">
                        <CardTitle className="text-sm font-medium">{responder.name}</CardTitle>
                        <Badge
                          variant="outline"
                          className={cn('w-fit text-xs', triggerStyle.badgeClass)}
                        >
                          {triggerLabel}
                        </Badge>
                      </div>
                      <CardAction>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-7 w-7"
                              aria-label={`Actions for ${responder.name}`}
                            >
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="w-32">
                            <DropdownMenuItem
                              onClick={() => {
                                setEditingResponder(responder);
                                setResponderSheetOpen(true);
                              }}
                            >
                              <Pencil className="mr-2 h-3.5 w-3.5" />
                              Edit
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                              className="text-destructive focus:text-destructive"
                              onClick={() => handleDeleteRequest(responder)}
                            >
                              <Trash2 className="mr-2 h-3.5 w-3.5" />
                              Delete
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </CardAction>
                    </CardHeader>

                    <CardContent className="pt-0">
                      <p className="line-clamp-3 text-xs text-muted-foreground">
                        {responder.message}
                      </p>
                      {responder.delay_seconds > 0 && (
                        <p className="mt-2 flex items-center gap-1 text-[10px] text-muted-foreground">
                          <Clock className="h-3 w-3" />
                          {responder.delay_seconds}s delay
                        </p>
                      )}
                    </CardContent>

                    <CardFooter className="flex items-center justify-between border-t pt-3">
                      <Badge
                        variant="outline"
                        className={cn(
                          'gap-1.5 text-xs',
                          responder.is_enabled
                            ? 'border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-800 dark:bg-emerald-950 dark:text-emerald-300'
                            : 'border-border text-muted-foreground',
                        )}
                      >
                        <span
                          className={cn(
                            'h-1.5 w-1.5 rounded-full',
                            responder.is_enabled ? 'bg-emerald-500' : 'bg-muted-foreground/40',
                          )}
                        />
                        {responder.is_enabled ? 'Enabled' : 'Disabled'}
                      </Badge>
                      {responder.times_triggered !== undefined && (
                        <span className="text-[11px] tabular-nums text-muted-foreground">
                          {responder.times_triggered} trigger{responder.times_triggered !== 1 ? 's' : ''}
                        </span>
                      )}
                    </CardFooter>
                  </Card>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* ── Auto Responder Sheet (add / edit) ─────────────────────────────────── */}
      <Sheet
        open={responderSheetOpen}
        onOpenChange={(open) => {
          if (!open) { setResponderSheetOpen(false); setEditingResponder(null); }
        }}
      >
        <SheetContent side="right" className="flex flex-col gap-0 p-0 sm:max-w-lg">
          <SheetHeader className="border-b px-6 py-4 pr-14">
            <SheetTitle>
              {editingResponder ? 'Edit Auto Responder' : 'New Auto Responder'}
            </SheetTitle>
            <SheetDescription className="text-xs">
              {editingResponder
                ? 'Update the settings for this auto responder.'
                : 'Configure an automatic reply for a specific trigger.'}
            </SheetDescription>
          </SheetHeader>

          {/* Form — rendered as inner component with access to parent callbacks */}
          <AutoResponderForm
            responder={editingResponder}
            onSubmit={handleAutoResponderSubmit}
            onCancel={() => { setResponderSheetOpen(false); setEditingResponder(null); }}
          />
        </SheetContent>
      </Sheet>

      {/* ── Delete confirmation AlertDialog ──────────────────────────────────── */}
      <AlertDialog
        open={deleteDialogOpen}
        onOpenChange={(open) => !open && setDeleteDialogOpen(false)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Auto Responder</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete{' '}
              <span className="font-medium">"{responderToDelete?.name}"</span>? This action
              cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setDeleteDialogOpen(false)}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={handleDeleteConfirm}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

    </div>
  );
};

// ─── Auto Responder Form (Sheet body + footer) ────────────────────────────────

interface AutoResponderFormProps {
  responder: AutoResponder | null;
  onSubmit: (data: Partial<AutoResponder>) => void;
  onCancel: () => void;
}

const AutoResponderForm: React.FC<AutoResponderFormProps> = ({
  responder,
  onSubmit,
  onCancel,
}) => {
  const [formData, setFormData] = useState<Partial<AutoResponder>>({
    name:         responder?.name         ?? '',
    trigger_type: responder?.trigger_type ?? 'outside_business_hours',
    message:      responder?.message      ?? '',
    conditions:   responder?.conditions   ?? {},
    is_enabled:   responder?.is_enabled   ?? true,
    delay_seconds:responder?.delay_seconds ?? 0,
  });

  // Re-sync when the responder prop changes (sheet opened for a different item)
  useEffect(() => {
    setFormData({
      name:         responder?.name         ?? '',
      trigger_type: responder?.trigger_type ?? 'outside_business_hours',
      message:      responder?.message      ?? '',
      conditions:   responder?.conditions   ?? {},
      is_enabled:   responder?.is_enabled   ?? true,
      delay_seconds:responder?.delay_seconds ?? 0,
    });
  }, [responder]);

  const handleSubmit = () => {
    if (!formData.name?.trim() || !formData.message?.trim()) {
      toast({
        title: 'Validation error',
        description: 'Name and message are required.',
        variant: 'destructive',
      });
      return;
    }
    onSubmit(formData);
  };

  return (
    <>
      {/* Scrollable body */}
      <div className="flex-1 overflow-y-auto px-6 py-5">
        <div className="space-y-5">

          {/* Name + Trigger type */}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="ar-name" className="text-xs font-medium">
                Name <span className="text-destructive">*</span>
              </Label>
              <Input
                id="ar-name"
                placeholder="e.g. After-hours reply"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="ar-trigger" className="text-xs font-medium">
                Trigger
              </Label>
              <Select
                value={formData.trigger_type}
                onValueChange={(v) => setFormData({ ...formData, trigger_type: v })}
              >
                <SelectTrigger id="ar-trigger">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {TRIGGER_TYPES.map((type) => (
                    <SelectItem key={type.value} value={type.value}>
                      {type.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Message */}
          <div className="space-y-1.5">
            <Label htmlFor="ar-message" className="text-xs font-medium">
              Message <span className="text-destructive">*</span>
            </Label>
            <textarea
              id="ar-message"
              rows={5}
              placeholder="We're currently outside our business hours. We'll get back to you as soon as possible."
              value={formData.message}
              onChange={(e) => setFormData({ ...formData, message: e.target.value })}
              className="flex min-h-[100px] w-full resize-none rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-xs transition-colors placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
            />
          </div>

          {/* Delay */}
          <div className="space-y-1.5">
            <Label htmlFor="ar-delay" className="text-xs font-medium">
              Delay (seconds)
            </Label>
            <Input
              id="ar-delay"
              type="number"
              min={0}
              value={formData.delay_seconds}
              onChange={(e) =>
                setFormData({ ...formData, delay_seconds: parseInt(e.target.value) || 0 })
              }
              className="w-40"
            />
            <p className="text-[11px] text-muted-foreground">
              Wait this many seconds before sending the auto reply (0 = instant)
            </p>
          </div>

          <Separator />

          {/* Enabled toggle */}
          <div className="flex items-center justify-between rounded-lg border px-4 py-3">
            <div className="space-y-0.5">
              <Label htmlFor="ar-enabled" className="cursor-pointer text-sm font-medium">
                Enabled
              </Label>
              <p className="text-xs text-muted-foreground">
                Allow this auto responder to fire automatically
              </p>
            </div>
            <Switch
              id="ar-enabled"
              checked={formData.is_enabled}
              onCheckedChange={(checked) => setFormData({ ...formData, is_enabled: checked })}
            />
          </div>

        </div>
      </div>

      {/* Footer */}
      <SheetFooter className="gap-2 border-t px-6 py-4">
        <Button variant="outline" onClick={onCancel}>
          Cancel
        </Button>
        <Button onClick={handleSubmit}>
          {responder ? 'Update' : 'Create'} Responder
        </Button>
      </SheetFooter>
    </>
  );
};

export default BusinessHoursPage;
